import { Injectable, Injector } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse, HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ContextSlugStore } from 'src/app/services/context-slug.store';
import { HighRiskStepUpService } from 'src/app/services/high-risk-step-up.service';
import { LOCALIZATION_LOCAL_STORAGE_KEY } from 'src/app/modules/i18n/translation.service';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private rawHttp: HttpClient;

  constructor(
    private httpBackend: HttpBackend,
    private contextSlugStore: ContextSlugStore,
    private injector: Injector,
  ) {
    // HttpClient via HttpBackend bypasses interceptors, preventing cycles
    this.rawHttp = new HttpClient(httpBackend);
  }

  private decodeExp(token?: string): number | undefined {
    if (!token) return undefined;
    try {
      const payload = JSON.parse(atob(token.split('.')[1] || ''));
      return typeof payload?.exp === 'number' ? payload.exp : undefined;
    } catch {
      return undefined;
    }
  }

  private isExpiringSoon(token?: string): boolean {
    const exp = this.decodeExp(token);
    if (!exp) return false;
    const now = Math.floor(Date.now() / 1000);
    return exp - now <= 60;
  }

  private refreshAccessToken(currentToken?: string, refreshToken?: string): Observable<string | undefined> {
    const url = `${environment.apiUrl}/auth/refresh`;
    const hasRt = typeof refreshToken === 'string' && refreshToken.length > 0;
    const headers = hasRt
      ? new HttpHeaders({ 'Content-Type': 'application/json' })
      : new HttpHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${currentToken || ''}` });
    const body = hasRt ? { refresh_token: refreshToken } : {};

    return this.rawHttp.post<any>(url, body, { headers }).pipe(
      switchMap((resp) => {
        const data = resp?.data ?? resp;
        const newToken = data?.token || data?.access_token;
        if (!newToken) return of(undefined);
        // persist
        localStorage.setItem('authToken', newToken);
        if (data?.refresh_token) {
          localStorage.setItem('refreshToken', data.refresh_token);
        }
        return of(newToken);
      }),
      catchError((error: HttpErrorResponse) => {
        const code = error?.error?.error_code || error?.error?.code;
        if (code === 'AUTH_MFA_IP_CHANGED') {
          return throwError(() => error);
        }
        return of(undefined);
      })
    );
  }

  private isIpChangeError(error: unknown): boolean {
    const httpErr = error as HttpErrorResponse;
    const code = httpErr?.error?.error_code || httpErr?.error?.code;
    return httpErr?.status === 401 && code === 'AUTH_MFA_IP_CHANGED';
  }

  private promptIpChangeAndRetry(
    req: HttpRequest<unknown>,
    next: HttpHandler,
    error: unknown,
  ): Observable<HttpEvent<unknown>> {
    if (req.headers.has('X-Mfa-Ip-Retry')) {
      return throwError(() => error);
    }
    let stepUp: HighRiskStepUpService | null = null;
    try {
      stepUp = this.injector.get(HighRiskStepUpService);
    } catch {
      stepUp = null;
    }
    if (!stepUp) {
      return throwError(() => error);
    }
    return stepUp.promptHighRiskMfa(true).pipe(
      switchMap((ok) => {
        if (!ok) {
          return throwError(() => error);
        }
        const fresh = localStorage.getItem('authToken') || '';
        const retried = req.clone({
          setHeaders: {
            Authorization: `Bearer ${fresh}`,
            'X-Mfa-Ip-Retry': '1',
          },
        });
        return next.handle(retried);
      })
    );
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    try {
      const rmmBase = environment.rmmApiProxied
        ? environment.rmmApiUrl.replace(/\/$/, '')
        : '';
      const isApiRequest =
        typeof req.url === 'string' &&
        (req.url.startsWith(environment.apiUrl) ||
          (!!rmmBase &&
            (req.url.startsWith(rmmBase) || req.url.startsWith(`/${rmmBase}`))));
      if (!isApiRequest) {
        return next.handle(req);
      }

      const attach = (token?: string) => {
        const headers: Record<string, string> = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        // Prefer URL context slug over session store — store can lag after partner switches.
        const fromUrl = (() => {
          try {
            const first = (window.location?.pathname || '').split('/').filter(Boolean)[0] || '';
            return /^[0-9a-f]{16}$/i.test(first) ? first.toLowerCase() : '';
          } catch {
            return '';
          }
        })();
        const contextSlug = fromUrl || this.contextSlugStore.get();
        const isMfaChallengeCall = /\/auth\/mfa\/(verify-totp|verify-email|setup-totp|setup-email|request)(\/|$|\?)/.test(
          req.url
        );
        // MFA-pending enroll/verify has no workspace yet — a leftover slug 403s AuthMiddleware.
        if (contextSlug && !isMfaChallengeCall) {
          headers['X-Context-Slug'] = contextSlug;
        }
        const locale = localStorage.getItem(LOCALIZATION_LOCAL_STORAGE_KEY) || undefined;
        if (locale) {
          headers['X-Locale'] = locale;
        }
        return req.clone({
          setHeaders: headers,
          withCredentials: true,
        });
      };

      const token = localStorage.getItem('authToken') || undefined;
      const refreshToken = localStorage.getItem('refreshToken') || undefined;
      const initialReq = attach(token);

      // Avoid refreshing on the refresh endpoint itself, and never refresh
      // MFA challenge calls (401 there means bad code or pending token).
      const isRefreshCall = req.url.startsWith(`${environment.apiUrl}/auth/refresh`);
      const isMfaChallengeCall = /\/auth\/mfa\/(verify-totp|verify-email|setup-totp|setup-email|request)(\/|$|\?)/.test(
        req.url
      );

      // Preemptive refresh if token is expiring soon
      if (!isRefreshCall && !isMfaChallengeCall && this.isExpiringSoon(token)) {
        return this.refreshAccessToken(token, refreshToken).pipe(
          switchMap((newToken) => next.handle(attach(newToken || token))),
          catchError((refreshErr) => {
            if (this.isIpChangeError(refreshErr)) {
              return this.promptIpChangeAndRetry(req, next, refreshErr);
            }
            return next.handle(initialReq);
          })
        );
      }

      return next.handle(initialReq).pipe(
        catchError((err: any) => {
          const httpErr = err as HttpErrorResponse;
          // IP changed since the JWT was issued. Refresh must not mint a token for the new IP.
          if (!isMfaChallengeCall && this.isIpChangeError(httpErr)) {
            return this.promptIpChangeAndRetry(req, next, err);
          }
          // On first 401 from our API, try a single refresh then retry once
          if (!isRefreshCall && !isMfaChallengeCall && httpErr && httpErr.status === 401) {
            return this.refreshAccessToken(token, refreshToken).pipe(
              switchMap((newToken) => {
                if (!newToken) return throwError(() => err);
                const retried = attach(newToken);
                return next.handle(retried);
              }),
              catchError((refreshErr) => {
                if (this.isIpChangeError(refreshErr)) {
                  return this.promptIpChangeAndRetry(req, next, refreshErr);
                }
                return throwError(() => err);
              })
            );
          }
          const deniedContext =
            httpErr?.status === 403 && httpErr?.error?.code === 'WORKSPACE_CONTEXT_DENIED';
          const slugFromUrl = (() => {
            try {
              const first = (window.location?.pathname || '').split('/').filter(Boolean)[0] || '';
              return /^[0-9a-f]{16}$/i.test(first) ? first.toLowerCase() : '';
            } catch {
              return '';
            }
          })();
          // A leftover session slug from another login 403s every call and leaves the dashboard empty.
          if (
            deniedContext &&
            !slugFromUrl &&
            this.contextSlugStore.get() &&
            !req.headers.has('X-Context-Retry')
          ) {
            this.contextSlugStore.set(null);
            const retried = initialReq.clone({
              headers: initialReq.headers.delete('X-Context-Slug').set('X-Context-Retry', '1'),
            });
            return next.handle(retried);
          }
          return throwError(() => err);
        })
      );
    } catch {
      return next.handle(req);
    }
  }
}


